// src/pages/Registration.js
import React, { useState, useEffect } from 'react';
import { db, app } from '../services/firebase';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PaymentForm from '../components/PaymentForm';
import MiniCalendar from '../components/MiniCalendar';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { groupDatesByMonth, formatDateForDisplay } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { pickL } from '../utils/i18nContent';
import { trackGoogleAdsConversion } from '../utils/googleAds';
import { trackMetaRegistrationConversion } from '../utils/metaPixel';
import './Registration.css';

// Fonctions pures (aucune dépendance au state du composant) : sorties du composant
// pour ne pas être recréées à chaque render, et pour satisfaire react-hooks/exhaustive-deps.
const isFormation = (course) => {
  const titleFr = (course?.title_i18n?.fr || course?.title || '');
  return titleFr.toLowerCase().includes('formation');
};

const filterCoursesForPlan = (list, plan) => {
  if (plan === 'essai' || plan === 'unique') return list;
  return list.filter(c => !isFormation(c));
};

// "15:00" -> "15h", "15:30" -> "15h30"
const formatHour = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hourNum = parseInt(h, 10);
  if (Number.isNaN(hourNum)) return time;
  return m && m !== '00' ? `${hourNum}h${m}` : `${hourNum}h`;
};

// Construit un libellé du type "Mercredi 15h - 17h, Vendredi 18h - 19h"
// à partir des dates enregistrées dans Firestore (dédupliqué par jour + horaire).
const buildScheduleLabel = (dates) => {
  const seen = new Map();
  (dates || []).forEach(d => {
    if (!d?.day) return;
    const key = `${d.day}|${d.startTime || ''}|${d.endTime || ''}`;
    if (!seen.has(key)) {
      seen.set(key, { day: d.day, startTime: d.startTime, endTime: d.endTime });
    }
  });
  return Array.from(seen.values())
    .map(s => (s.startTime && s.endTime)
      ? `${s.day} ${formatHour(s.startTime)} - ${formatHour(s.endTime)}`
      : s.day
    )
    .join(', ');
};

const DAY_ORDER = {
  'lundi': 0, 'mardi': 1, 'mercredi': 2, 'jeudi': 3,
  'vendredi': 4, 'samedi': 5, 'dimanche': 6
};

// Renvoie {dayIndex, minutes} du créneau le plus tôt dans la semaine pour un cours,
// afin de pouvoir trier les cours par jour puis par heure.
const getEarliestSlot = (dates) => {
  let best = null;
  (dates || []).forEach(d => {
    if (!d?.day) return;
    const dayIndex = DAY_ORDER[d.day.trim().toLowerCase()];
    if (dayIndex === undefined) return;
    const [h, m] = (d.startTime || '00:00').split(':').map(Number);
    const minutes = (h || 0) * 60 + (m || 0);
    if (!best || dayIndex < best.dayIndex || (dayIndex === best.dayIndex && minutes < best.minutes)) {
      best = { dayIndex, minutes };
    }
  });
  return best || { dayIndex: 99, minutes: 0 };
};

const Registration = () => {
  const { t, i18n } = useTranslation('registration');
  const location = useLocation();
  const [showForm, setShowForm] = useState(location.state?.prefillData || false);
  const [selectedPlan, setSelectedPlan] = useState(location.state?.selectedPlan || null);
  const [registrationType, setRegistrationType] = useState('child');
  // imageConsent est désormais porté par CHAQUE personne (droit à l'image par enfant),
  // et non plus au niveau global de l'inscription.
  // null = pas encore répondu ; true/false = choix explicite du parent pour cette personne
  const [persons, setPersons] = useState([{ lastName: '', firstName: '', dateOfBirth: '', imageConsent: null }]);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    address: '',
    insuranceIncluded: false,
    parentLastName: '',
    parentFirstName: '',
    agreedToCancellationPolicy: false,
  });

  // Un seul cours pour l'essai/cours unique, plusieurs possibles pour les autres formules
  // (aligné sur le même schéma Firestore que l'app mobile : selectedCourseIds / selectedCourses).
  const [selectedCourseIds, setSelectedCourseIds] = useState(
    location.state?.courseId ? [location.state.courseId] : []
  );

  const [allCourses, setAllCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [selectedDate, setSelectedDate] = useState(location.state?.selectedDate || '');

  // Liste d'attente (cours marqué complet — champ isFull, CourseForm.js/CourseEdit.js)
  const [waitlistModal, setWaitlistModal] = useState({ open: false, courseId: null, courseTitle: '' });
  const [waitlistForm, setWaitlistForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  const isSinglePlan = selectedPlan === 'essai' || selectedPlan === 'unique';
  const isCard10Plan = selectedPlan === 'carte10';

  // Si on repasse sur une formule à cours unique alors que plusieurs cours étaient
  // sélectionnés (formule précédente), on ne garde que le premier.
  useEffect(() => {
    if (isSinglePlan) {
      setSelectedCourseIds(prev => (prev.length > 1 ? prev.slice(0, 1) : prev));
    }
  }, [isSinglePlan]);

  const toggleCourseSelection = (courseId) => {
    setSelectedCourseIds(prev => {
      if (isSinglePlan) return [courseId];
      return prev.includes(courseId) ? prev.filter(id => id !== courseId) : [...prev, courseId];
    });
  };

  useEffect(() => {
    if (location.state?.prefillData) {
      setShowForm(true);
      setSelectedPlan('essai');
      setSelectedCourseIds([location.state.courseId]);
      setFormData(prev => ({
        ...prev,
        insuranceIncluded: false,
        agreedToCancellationPolicy: false
      }));
      setSelectedDate(location.state.selectedDate);
    }
    if (location.state?.joinWaitlist) {
      setShowForm(true);
      setWaitlistModal({
        open: true,
        courseId: location.state.courseId,
        courseTitle: location.state.courseTitle || ''
      });
    }
  }, [location.state]);

  const handleTypeChange = (e) => {
    setRegistrationType(e.target.value);
    setPersons([{ lastName: '', firstName: '', dateOfBirth: '' }]);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleInsuranceChange = (e) => {
    setFormData(prev => ({ ...prev, insuranceIncluded: e.target.checked }));
  };

  // ── Liste d'attente (cours complet) ────────────────────────────────────
  const openWaitlist = (courseId, courseTitle) => {
    setWaitlistError('');
    setWaitlistSuccess(false);
    setWaitlistForm({ firstName: '', lastName: '', email: '', phone: '' });
    setWaitlistModal({ open: true, courseId, courseTitle });
  };

  const closeWaitlist = () => {
    setWaitlistModal({ open: false, courseId: null, courseTitle: '' });
  };

  const handleWaitlistFieldChange = (e) => {
    const { name, value } = e.target;
    setWaitlistForm(prev => ({ ...prev, [name]: value }));
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistForm.firstName.trim() || !waitlistForm.lastName.trim() || !waitlistForm.email.trim()) {
      setWaitlistError(t('waitlist.missingFields', { defaultValue: 'Merci de renseigner au moins ton nom, prénom et e-mail.' }));
      return;
    }
    setWaitlistSubmitting(true);
    setWaitlistError('');
    try {
      await addDoc(collection(db, 'waitlist'), {
        courseId: waitlistModal.courseId,
        courseTitle: waitlistModal.courseTitle,
        firstName: waitlistForm.firstName.trim(),
        lastName: waitlistForm.lastName.trim(),
        email: waitlistForm.email.trim(),
        phone: waitlistForm.phone.trim(),
        locale: i18n.language || 'fr',
        status: 'pending',
        createdAt: serverTimestamp()
      });
      setWaitlistSuccess(true);
    } catch (error) {
      console.error("Erreur d'inscription à la liste d'attente:", error);
      setWaitlistError(t('waitlist.error', { defaultValue: "Une erreur s'est produite, réessaie dans un instant." }));
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const pricingPlanIds = ['essai', 'unique', 'carte10'];
  const subscriptionPrices = {
    essai: 10,
    unique: 12,
    carte10: 95
  };
  // Nombre de "crédits" par formule, identique à l'app mobile (999 = illimité)
  const creditsPerPlan = {
    essai: 1,
    unique: 1,
    carte10: 10,
    abonnement: 999
  };

  // Grille tarifaire de l'abonnement, par palier (nombre de cours/semaine).
  // - annuel = 8x le tarif mensuel (payé en 1 fois)
  // - semestre 1 (sept-déc, 4 mois) et semestre 2 (jan-juin, 6 mois) = 90% du
  //   tarif mensuel × nombre de mois de la période (donc 2 montants différents)
  // Reste synchronisé avec _tierPricing dans mes_inscriptions.dart (app mobile).
  const tierPricing = {
    1: { monthly: 40, annual: 320, semester1: 144, semester2: 216 }, // Bronze
    2: { monthly: 70, annual: 560, semester1: 252, semester2: 378 }, // Argent
    3: { monthly: 90, annual: 720, semester1: 324, semester2: 486 }  // Gold (3 cours/semaine ou +)
  };
  const tierName = { 1: t('plans.tiers.1.name'), 2: t('plans.tiers.2.name'), 3: t('plans.tiers.3.name') };

  const isAbonnementPlan = selectedPlan === 'abonnement';
  const [abonnementFrequency, setAbonnementFrequency] = useState('monthly'); // monthly | semester1 | semester2 | annual
  const abonnementTier = selectedCourseIds.length <= 1 ? 1 : (selectedCourseIds.length === 2 ? 2 : 3);
  const abonnementPrice = tierPricing[abonnementTier][abonnementFrequency];
  // Prix effectif de la formule sélectionnée (gère le cas dynamique 'abonnement').
  const effectivePlanPrice = isAbonnementPlan ? abonnementPrice : (subscriptionPrices[selectedPlan] || 0);

  const handlePaymentMethodChange = (method) => setPaymentMethod(method);

  const saveToFirestore = async () => {
    try {
      const baseAmount = selectedPlan ? effectivePlanPrice * persons.length : 0;
      const insuranceAmount = formData.insuranceIncluded ? 25 * persons.length : 0;
      const totalAmount = baseAmount + insuranceAmount;

      const selectedCourseObjs = selectedCourseIds
        .map(id => courses.find(c => c.id === id))
        .filter(Boolean);
      const first = selectedCourseObjs[0] || null;
      const firstTitle = first ? pickL(first.title_i18n ?? first.title, i18n.language, 'fr') : '';

      const selCourses = selectedCourseObjs.map(c => ({
        id: c.id,
        title: pickL(c.title_i18n ?? c.title, i18n.language, 'fr')
      }));

      const creditsTotal = creditsPerPlan[selectedPlan] ?? 999;
      const creditsUsed = isSinglePlan ? 1 : 0;
      const creditsRemaining = creditsTotal >= 999 ? 999 : Math.max(creditsTotal - creditsUsed, 0);

      // Pour les formules à cours unique (essai/unique), une seule date choisie.
      // Pour les abonnements (hors carte 10), toutes les dates futures des cours sélectionnés.
      // Pour la carte 10, les dates seront ajoutées au fil des présences (aucune à l'avance).
      const selectedDates = isSinglePlan
        ? [selectedDate]
        : (isCard10Plan
          ? []
          : Array.from(new Set(
              selectedCourseObjs.flatMap(c => (c.dates || []).map(d => new Date(d).toISOString().split('T')[0]))
            )).sort());

      const freqLabel = { monthly: 'mensuel', semester1: 'semestre 1', semester2: 'semestre 2', annual: 'annuel' }[abonnementFrequency];
      const planDisplayName = isAbonnementPlan
        ? `Abonnement ${tierName[abonnementTier]} (${freqLabel})`
        : (selectedPlan ? t(`plans.items.${selectedPlan}.name`) : null);

      // Date/heure ISO complète du cours choisi (essai/unique uniquement) —
      // nécessaire pour que les rappels automatiques (J-1, jour même) sachent
      // quelle inscription cibler. `first.dates` contient les créneaux
      // complets (avec heure) avant le découpage en date seule pour l'affichage.
      const matchingFullDateTime = isSinglePlan
        ? (first?.dates || []).find(d => d && d.split('T')[0] === selectedDate) || null
        : null;

      const registrationData = {
        planDetails: {
          id: selectedPlan,
          name: planDisplayName,
          type: selectedPlan ? t(`plans.items.${selectedPlan}.type`) : null,
          price: effectivePlanPrice
        },
        plan: selectedPlan,
        planName: planDisplayName,
        planType: isSinglePlan ? 'single' : 'formula',
        ...(isAbonnementPlan ? {
          abonnementTier,
          abonnementTierName: tierName[abonnementTier],
          paymentFrequency: abonnementFrequency
        } : {}),
        // Compatibilité: premier cours sélectionné, exposé aussi en single champ
        courseId: first?.id || '',
        courseTitle: firstTitle,
        // Champs multi-cours (mêmes noms que l'app mobile et l'admin panel)
        selectedCourseIds: selectedCourseIds,
        selectedCourses: selCourses,
        selectedDate: isSinglePlan ? selectedDate : null,
        selectedDateTime: matchingFullDateTime,
        selectedDates,
        allDates: !isSinglePlan && !isCard10Plan,
        creditsTotal,
        creditsUsed,
        creditsRemaining,
        consumedLessons: isSinglePlan ? [{
          courseId: first?.id || '',
          courseTitle: firstTitle,
          dateKey: selectedDate,
          source: 'registration',
          timestamp: new Date().toISOString()
        }] : [],
        paymentStatus: 'pending',
        paymentDetails: {
          baseAmount,
          insuranceAmount,
          totalAmount,
          paymentMethod: selectedPlan === 'essai' ? 'mollie' : paymentMethod
        },
        persons: persons.map(person => ({
          firstName: person.firstName,
          lastName: person.lastName,
          dateOfBirth: person.dateOfBirth,
          imageConsent: person.imageConsent // droit à l'image par personne (true/false)
        })),
        contactInfo: {
          email: formData.email,
          phone: formData.phone,
          address: !isSinglePlan ? formData.address : null,
        },
        parentInfo: registrationType === 'child' ? {
          parentFirstName: formData.parentFirstName,
          parentLastName: formData.parentLastName
        } : null,
        insuranceIncluded: formData.insuranceIncluded,
        agreedToCancellationPolicy: formData.agreedToCancellationPolicy,
        // Le droit à l'image est désormais par personne (voir persons[].imageConsent).
        // On garde ce résumé au niveau de l'inscription uniquement pour du filtrage rapide
        // côté admin (ex: colonne "Image" dans la liste des inscriptions) — ce n'est PAS
        // la source de vérité si les personnes ont des réponses différentes.
        imageRightsConsentAll: persons.every(p => p.imageConsent === true),
        registrationType,
        locale: i18n.language,
        createdAt: serverTimestamp(),
        fromSchedule: location.state?.prefillData || false,
        trialDeductible: selectedPlan === 'essai',
        registrationSource: 'site',   // ← source fiable pour distinguer web vs app vs admin
        source: 'site'                  // alias pour compatibilité Registrations.js
      };

      const registrationRef = await addDoc(collection(db, 'registrations'), registrationData);
      const registrationId = registrationRef.id;

      trackGoogleAdsConversion();
      trackMetaRegistrationConversion({
        value: totalAmount,
        planId: selectedPlan,
        courseIds: selectedCourseIds
      });

      if (registrationType === 'child') {
        const parentUserData = {
          firstName: formData.parentFirstName,
          lastName: formData.parentLastName,
          email: formData.email,
          phone: formData.phone,
          role: 'Parent',
          registrationSource: 'site',
          createdAt: serverTimestamp()
        };
        const parentRef = await addDoc(collection(db, 'users'), parentUserData);
        const parentId = parentRef.id;

        const children = [];

        for (const person of persons) {
          const childData = {
            firstName: person.firstName,
            lastName: person.lastName,
            dateOfBirth: person.dateOfBirth ? Timestamp.fromDate(new Date(person.dateOfBirth)) : null,
            role: 'Élève',
            parentEmail: formData.email,
            parentPhone: formData.phone,
            registrationSource: 'site',
            createdAt: Timestamp.now()
          };
          const childRef = await addDoc(collection(db, 'users'), childData);

          children.push({ 
            id: childRef.id,
            firstName: person.firstName,
            lastName: person.lastName
          });

          await addDoc(collection(db, 'students'), {
            userId: childRef.id,
            firstName: person.firstName,
            lastName: person.lastName,
            dateOfBirth: person.dateOfBirth,
            parentId: parentId,
            createdAt: serverTimestamp()
          });
        }

        const parentData = {
          parentId: parentId,
          parentFirstName: formData.parentFirstName,
          parentLastName: formData.parentLastName,
          email: formData.email,
          phone: formData.phone,
          children: children,
          registrationId: registrationId,
          createdAt: serverTimestamp()
        };
        await addDoc(collection(db, 'parents'), parentData);
      } else {
        for (const person of persons) {
          const adultData = {
            firstName: person.firstName,
            lastName: person.lastName,
            dateOfBirth: person.dateOfBirth ? Timestamp.fromDate(new Date(person.dateOfBirth)) : null,
            email: formData.email,
            phone: formData.phone,
            role: 'Élève adulte',
            registrationSource: 'site',
            createdAt: Timestamp.now()
          };
          const adultRef = await addDoc(collection(db, 'users'), adultData);

          await addDoc(collection(db, 'students'), {
            userId: adultRef.id,
            firstName: person.firstName,
            lastName: person.lastName,
            dateOfBirth: person.dateOfBirth,
            createdAt: serverTimestamp()
          });
        }
      }

      return registrationId;
    } catch (error) {
      console.error("Erreur d'enregistrement: ", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesRef = collection(db, 'dance_courses');
        const querySnapshot = await getDocs(coursesRef);
        const coursesList = [];

        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const daysLabel = buildScheduleLabel(data.dates);
          const dates = (data.dates || []).map(d => d.date);
          const sortKey = getEarliestSlot(data.dates);
          coursesList.push({
            id: docSnap.id,
            ...data,
            dates,
            daysLabel,
            _sortDayIndex: sortKey.dayIndex,
            _sortMinutes: sortKey.minutes
          });
        });

        coursesList.sort((a, b) =>
          a._sortDayIndex - b._sortDayIndex || a._sortMinutes - b._sortMinutes
        );

        setAllCourses(coursesList);

        setCourses(filterCoursesForPlan(coursesList, selectedPlan));

        setLoadingCourses(false);
      } catch (err) {
        console.error("Erreur de chargement des cours: ", err);
        setError(t('errors.loadCourses'));
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [t, selectedPlan]);

  useEffect(() => {
    if (!allCourses.length) return;

    setCourses(filterCoursesForPlan(allCourses, selectedPlan));
  }, [selectedPlan, allCourses]);

  useEffect(() => {
    if (selectedPlan === 'essai' || selectedPlan === 'unique') {
      setLoadingDates(true);
      const currentCourseId = selectedCourseIds[0];

      const fetchDates = async () => {
        try {
          if (currentCourseId && courses.length > 0) {
            const course = courses.find(c => c.id === currentCourseId);
            if (course && course.dates) {
              const today = new Date().toISOString().split('T')[0];
              const futureDates = course.dates
                .filter(date => date && date.split('T')[0] >= today)
                .map(date => date.split('T')[0])
                .sort();

              setAvailableDates(futureDates);
            } else {
              setAvailableDates([]);
            }
          }
        } catch (err) {
          console.error("Erreur de chargement des dates: ", err);
        } finally {
          setLoadingDates(false);
        }
      };

      fetchDates();
    } else {
      setAvailableDates([]);
    }
  }, [selectedCourseIds, selectedPlan, courses]);

  const addPerson = () => setPersons([...persons, { lastName: '', firstName: '', dateOfBirth: '', imageConsent: null }]);
  const removePerson = (index) => {
    if (persons.length > 1) {
      const newPersons = [...persons];
      newPersons.splice(index, 1);
      setPersons(newPersons);
    }
  };
  const handlePersonChange = (index, e) => {
    const { name, value } = e.target;
    const newPersons = [...persons];
    newPersons[index] = { ...newPersons[index], [name]: value };
    setPersons(newPersons);
  };
  const handlePersonImageConsent = (index, value) => {
    const newPersons = [...persons];
    newPersons[index] = { ...newPersons[index], imageConsent: value };
    setPersons(newPersons);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allPersonsValid = persons.every(person => person.lastName && person.firstName && person.dateOfBirth);
    if (!allPersonsValid) {
      alert(t('errors.fillPersons', { type: registrationType === 'child' ? t('labels.child').toLowerCase() : t('labels.adult').toLowerCase() }));
      return;
    }
    const allImageConsentAnswered = persons.every(person => person.imageConsent !== null);
    if (!allImageConsentAnswered) {
      alert(t('imageConsent.requiredPerPerson', { defaultValue: "Merci d'indiquer le droit à l'image pour chaque personne inscrite." }));
      return;
    }
    if (registrationType === 'child' && (!formData.parentLastName || !formData.parentFirstName)) {
      alert(t('errors.fillParent'));
      return;
    }
    if (selectedCourseIds.length === 0) {
      alert(t('errors.selectCourse'));
      return;
    }
    if ((selectedPlan === 'essai' || selectedPlan === 'unique') && !selectedDate) {
      alert(t('errors.selectDate'));
      return;
    }

    if (!isSinglePlan && !formData.address.trim()) {
      alert(t('errors.fillAddress', { defaultValue: "L'adresse est obligatoire pour cette formule (nécessaire pour l'assurance)." }));
      return;
    }
    if (!formData.agreedToCancellationPolicy) {
      alert(t('cancellationPolicy.checkbox') + ' ' + (t('errors.required') || '(Champ obligatoire)'));
      return;
    }
    setIsSubmitting(true);
    try {
      const registrationId = await saveToFirestore();
      if (!registrationId) {
        alert(t('submit.failToast'));
        return;
      }

      if (selectedPlan === 'essai') {
        // Paiement immédiat via Mollie pour le cours d'essai — le site ne
        // connecte pas les visiteurs, on établit donc une session anonyme
        // juste avant l'appel, uniquement pour satisfaire l'authentification
        // requise par la Cloud Function (protection contre les abus).
        const auth = getAuth(app);
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }

        const functions = getFunctions(app, 'europe-west1');
        const createPayment = httpsCallable(functions, 'createPayment');
        const response = await createPayment({
          type: 'registration',
          refId: registrationId,
          amount: totalAmount,
          description: "Cours d'essai MKD Studio"
        });

        const checkoutUrl = response.data?.checkoutUrl;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return; // on quitte la page vers Mollie, pas besoin de continuer
        }
        alert(t('submit.paymentLinkError', { defaultValue: "Inscription enregistrée, mais le lien de paiement n'a pas pu être généré. Nous vous contacterons." }));
      }

      setSubmitSuccess(true);
      alert(t('submit.successToast'));
    } catch (error) {
      console.error("Erreur complète:", error);
      alert(t('submit.error', { msg: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const baseAmount = selectedPlan ? effectivePlanPrice * persons.length : 0;
  const insuranceAmount = formData.insuranceIncluded ? 25 * persons.length : 0;
  const totalAmount = baseAmount + insuranceAmount;

  return (
    <div className="registration-container">
      <h1 className="registration-title">{t('title')}</h1>

      {!showForm ? (
        <div className="pricing-section">
          <h2 className="section-subtitle">{t('plans.categoryPerCourse', { defaultValue: 'Par cours' })}</h2>

          <div className="plans-grid">
            {pricingPlanIds.map(planId => (
              <div key={planId} className="plan-card">
                <div className="plan-header">
                  <h3 className="plan-name">{t(`plans.items.${planId}.name`)}</h3>
                  {t(`plans.items.${planId}.details`, { defaultValue: '' }) && (
                    <p className="plan-details">{t(`plans.items.${planId}.details`)}</p>
                  )}
                </div>
                <p className="plan-price">{t(`plans.items.${planId}.priceDesc`)}</p>
                {planId === 'essai' && (
                  <p className="plan-note">{t('plans.items.essai.note')}</p>
                )}
                <button 
                  className="select-button"
                  onClick={() => {
                    setSelectedPlan(planId);
                    setShowForm(true);
                    if (planId === 'essai') {
                      setFormData(prev => ({ ...prev, insuranceIncluded: false }));
                    }
                  }}
                >
                  {t('plans.cta')}
                </button>
              </div>
            ))}
          </div>

          <h2 className="section-subtitle" style={{ marginTop: '40px' }}>{t('plans.categorySubscriptions', { defaultValue: 'Abonnements' })}</h2>

          <div className="tier-columns-grid">
            {[1, 2, 3].map(tier => (
              <div key={tier} className="tier-column">
                <div className="tier-column-header">
                  <h3 className="tier-column-name">{t(`plans.tiers.${tier}.name`)}</h3>
                  <p className="tier-column-details">{t(`plans.tiers.${tier}.details`)}</p>
                </div>
                <div className="tier-price-list">
                  <div className="tier-price-row">
                    <span className="tier-price-label">{t('plans.frequency.monthly')}</span>
                    <span className="tier-price-value">{tierPricing[tier].monthly}€{t('plans.frequency.perMonth')}</span>
                  </div>
                  <div className="tier-price-row">
                    <span className="tier-price-label">{t('plans.frequency.semester1')}</span>
                    <span className="tier-price-value">{tierPricing[tier].semester1}€</span>
                  </div>
                  <div className="tier-price-row">
                    <span className="tier-price-label">{t('plans.frequency.semester2')}</span>
                    <span className="tier-price-value">{tierPricing[tier].semester2}€</span>
                  </div>
                  <div className="tier-price-row tier-price-row--annual">
                    <span className="tier-price-label">{t('plans.frequency.annual')}</span>
                    <span className="tier-price-value">{tierPricing[tier].annual}€</span>
                  </div>
                </div>
                <button
                  className="select-button"
                  onClick={() => {
                    setSelectedPlan('abonnement');
                    setShowForm(true);
                  }}
                >
                  {t('plans.cta')}
                </button>
              </div>
            ))}
          </div>

          <div className="plans-note">
            <div className="plans-note-content">
              <span className="plans-note-icon">💰</span>
              <div className="plans-note-text">
                <strong>💬 {t('plans.financialNote.title', { defaultValue: "Parlons-en ensemble !" })}</strong>
                <p>{t('plans.financialNote.text', { defaultValue: "Si les tarifs ne sont pas dans vos moyens, n'hésitez pas à nous en parler. Nous trouverons ensemble une solution adaptée à votre situation." })}</p>
              </div>
            </div>
          </div>

          <div className="cancellation-notice">
            <h4>❗ {t('plans.cancellationNote.title', { defaultValue: "Désistement & annulation" })}</h4>
            <p>{t('plans.cancellationNote.text', { defaultValue: "La réservation d'un cours d'essai est ferme, mais flexible sur la date. Votre place est confirmée après paiement. Les frais de réservation ne sont pas remboursables. En cas d'empêchement, vous pouvez reporter votre cours à une autre date en nous prévenant au minimum 24h à l'avance. Si vous vous inscrivez ensuite à un abonnement, ce montant est déduit de votre abonnement. ⚠️ Toute absence sans prévenir, ou annulation moins de 24h à l'avance, sera considérée comme un cours d'essai utilisé (non reportable)." })}</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-header">
            <button 
              type="button" 
              className="back-button"
              onClick={() => setShowForm(false)}
            >
              &larr; {t('backToPlans')}
            </button>
          </div>

          <div className="form-section">
            <h3 className="section-subtitle">{t('type.title')}</h3>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="registrationType"
                  value="child"
                  checked={registrationType === 'child'}
                  onChange={handleTypeChange}
                />
                <span>{t('type.child')}</span>
              </label>

              <label className="radio-label">
                <input
                  type="radio"
                  name="registrationType"
                  value="adult"
                  checked={registrationType === 'adult'}
                  onChange={handleTypeChange}
                />
                <span>{t('type.adult')}</span>
              </label>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-subtitle">
              {registrationType === 'child' ? t('persons.titleChildren') : t('persons.titleAdults')}
            </h3>

            {persons.map((person, index) => (
              <div key={index} className="person-form">
                <div className="form-group-header">
                  <h4>{registrationType === 'child' ? `${t('labels.child')} ${index + 1}` : `${t('labels.adult')} ${index + 1}`}</h4>
                  {persons.length > 1 && (
                    <button 
                      type="button" 
                      className="remove-button"
                      onClick={() => removePerson(index)}
                    >
                      {t('persons.remove')}
                    </button>
                  )}
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('fields.lastName')} *</label>
                    <input 
                      type="text" 
                      name="lastName" 
                      value={person.lastName} 
                      onChange={(e) => handlePersonChange(index, e)} 
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('fields.firstName')} *</label>
                    <input 
                      type="text" 
                      name="firstName" 
                      value={person.firstName} 
                      onChange={(e) => handlePersonChange(index, e)} 
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('fields.dateOfBirth')} *</label>
                    <input 
                      type="date" 
                      name="dateOfBirth" 
                      value={person.dateOfBirth} 
                      onChange={(e) => handlePersonChange(index, e)} 
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="image-consent-per-person" style={{ marginTop: 12 }}>
                  <p className="info-text" style={{ marginTop: 0, marginBottom: 8, fontSize: '0.9em' }}>
                    {t('imageConsent.descriptionPerPerson', {
                      defaultValue: "Droit à l'image pour cette personne : dans le cadre de nos cours et événements, il nous arrive de prendre des photos ou vidéos publiées ensuite sur nos réseaux sociaux (Instagram, Facebook) à des fins de communication."
                    })}
                  </p>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={`imageConsent-${index}`}
                        checked={person.imageConsent === true}
                        onChange={() => handlePersonImageConsent(index, true)}
                        required
                      />
                      {t('imageConsent.yes', { defaultValue: "Oui, j'autorise" })}
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={`imageConsent-${index}`}
                        checked={person.imageConsent === false}
                        onChange={() => handlePersonImageConsent(index, false)}
                        required
                      />
                      {t('imageConsent.no', { defaultValue: "Non, je n'autorise pas" })}
                    </label>
                  </div>
                </div>
              </div>
            ))}

            <button 
              type="button" 
              className="add-button"
              onClick={addPerson}
            >
              + {t('persons.addAnother', { type: registrationType === 'child' ? t('labels.child').toLowerCase() : t('labels.adult').toLowerCase() })}
            </button>
          </div>

          {registrationType === 'child' && (
            <div className="form-section">
              <h3 className="section-subtitle">{t('parent.title')}</h3>

              <div className="form-grid">
                <div className="form-group">
                  <label>{t('fields.lastName')} *</label>
                  <input 
                    type="text" 
                    name="parentLastName" 
                    value={formData.parentLastName} 
                    onChange={handleChange} 
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t('fields.firstName')} *</label>
                  <input 
                    type="text" 
                    name="parentFirstName" 
                    value={formData.parentFirstName} 
                    onChange={handleChange} 
                    required
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3 className="section-subtitle">{t('contact.title')}</h3>

            <div className="form-grid">
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>{t('fields.phone')}</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  className="form-input"
                />
              </div>

              {!isSinglePlan && (
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>{t('fields.address', { defaultValue: 'Adresse complète' })} *</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={t('fields.addressPlaceholder', { defaultValue: 'Rue, numéro, code postal, ville' })}
                    required={!isSinglePlan}
                    className="form-input"
                  />
                  <p className="info-text" style={{ marginLeft: 0 }}>
                    {t('fields.addressNote', { defaultValue: "Information nécessaire pour l'assurance couvrant les cours." })}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-subtitle">{t('course.title')}</h3>
            {error && <p className="error-text">{error}</p>}
            {!isSinglePlan && (
              <p className="info-text">{t('course.multiSelectHint', { defaultValue: 'Vous pouvez sélectionner plusieurs cours.' })}</p>
            )}
            {loadingCourses ? (
              <p className="loading-text">{t('course.loading')}</p>
            ) : courses.length > 0 ? (
              <div className="course-card-grid">
                {courses.map(course => {
                  const isSelected = selectedCourseIds.includes(course.id);
                  const courseTitle = pickL(course.title_i18n ?? course.title, i18n.language, 'fr');
                  if (course.isFull) {
                    return (
                      <div key={course.id} className="course-select-card course-select-card--full" aria-disabled="true">
                        <span className="course-select-card-full-badge">
                          🔒 {t('course.full', { defaultValue: 'Complet' })}
                        </span>
                        <span className="course-select-card-title">{courseTitle}</span>
                        {course.daysLabel && (
                          <span className="course-select-card-time">{course.daysLabel}</span>
                        )}
                        <button
                          type="button"
                          className="course-select-card-waitlist-button"
                          onClick={() => openWaitlist(course.id, courseTitle)}
                        >
                          {t('buttons.waitlist', { defaultValue: "Rejoindre la liste d'attente" })}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <button
                      type="button"
                      key={course.id}
                      className={`course-select-card ${isSelected ? 'course-select-card--selected' : ''}`}
                      onClick={() => toggleCourseSelection(course.id)}
                    >
                      <span className="course-select-card-title">
                        {courseTitle}
                      </span>
                      {course.daysLabel && (
                        <span className="course-select-card-time">{course.daysLabel}</span>
                      )}
                      <span className="course-select-card-check">{isSelected ? '✓' : ''}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="error-text">{t('course.empty')}</p>
            )}
            {selectedCourseIds.length > 0 && location.state?.courseTitle && selectedCourseIds[0] === location.state.courseId && (
              <p className="info-text">
                {t('course.selectedInfo')}: {location.state.courseTitle} - {location.state.sessionTime}
                {location.state.teachers && ` ${t('course.with')} ${location.state.teachers}`}
              </p>
            )}
            {isAbonnementPlan && selectedCourseIds.length > 0 && (
              <div className="tier-badge">
                {t('course.tierNote', { tier: tierName[abonnementTier], count: selectedCourseIds.length })}
              </div>
            )}
          </div>

          {isAbonnementPlan && (
            <div className="form-section">
              <h3 className="section-subtitle">{t('plans.frequency.title')}</h3>
              <div className="frequency-grid">
                {['monthly', 'semester1', 'semester2', 'annual'].map(freq => (
                  <button
                    type="button"
                    key={freq}
                    className={`frequency-card ${abonnementFrequency === freq ? 'frequency-card--selected' : ''}`}
                    onClick={() => setAbonnementFrequency(freq)}
                  >
                    <span className="frequency-card-title">{t(`plans.frequency.${freq}`)}</span>
                    <span className="frequency-card-price">{tierPricing[abonnementTier][freq]}€</span>
                    {freq === 'annual' && (
                      <span className="frequency-card-hint">{t('plans.frequency.annualHint')}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(selectedPlan === 'essai' || selectedPlan === 'unique') && (
            <div className="form-section">
              <h3 className="section-subtitle">{t('dates.title')}</h3>

              {loadingDates ? (
                <p className="loading-text">{t('dates.loading')}</p>
              ) : availableDates.length > 0 ? (
                <div className="calendar-section">
                  <div className="mini-calendar">
                    {groupDatesByMonth(availableDates).map(({ year, month, dates, monthLabel }, idx) => (
                      <div key={idx} className="month-group">
                        <h5>{monthLabel}</h5>
                        <MiniCalendar
                          year={year}
                          month={month}
                          highlights={dates}
                          selectedDate={selectedDate}
                          onSelectDate={setSelectedDate}
                        />
                      </div>
                    ))}
                  </div>

                  {selectedDate && (
                    <div className="selected-date-info">
                      <p>
                        <strong>{t('dates.selected')}:</strong> {formatDateForDisplay(selectedDate)}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="error-text">{t('dates.empty')}</p>
              )}
            </div>
          )}

          {selectedPlan !== 'essai' && selectedPlan !== 'unique' && (
            <div className="form-section">
              <h3 className="section-subtitle">{t('insurance.title')}</h3>
              <div className="insurance-card">
                <label className="insurance-label">
                  <input
                    type="checkbox"
                    checked={formData.insuranceIncluded}
                    onChange={handleInsuranceChange}
                    className="insurance-checkbox"
                  />
                  <span>{t('insurance.label')}</span>
                </label>
                <p className="insurance-description">
                  {t('insurance.description')}
                </p>
              </div>
            </div>
          )}

          <div className="form-section">
            <div className="cancellation-notice">
              <h4>❗ {t('cancellationPolicy.title', { defaultValue: "Désistement & annulation" })}</h4>
              <p>{t('cancellationPolicy.text', { defaultValue: "La réservation d'un cours d'essai est ferme, mais flexible sur la date. Votre place est confirmée après paiement. Les frais de réservation ne sont pas remboursables. En cas d'empêchement, vous pouvez reporter votre cours à une autre date en nous prévenant au minimum 24h à l'avance. Si vous vous inscrivez ensuite à un abonnement, ce montant est déduit de votre abonnement. ⚠️ Toute absence sans prévenir, ou annulation moins de 24h à l'avance, sera considérée comme un cours d'essai utilisé (non reportable)." })}</p>
            </div>
            
            <div className="cancellation-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="agreedToCancellationPolicy"
                  checked={formData.agreedToCancellationPolicy}
                  onChange={handleChange}
                  required
                />
                <span>{t('cancellationPolicy.checkbox', { defaultValue: "☑️ J'ai pris connaissance des conditions d'annulation et je m'engage à prévenir en cas d'empêchement." })}</span>
              </label>
            </div>
          </div>

          {selectedPlan && (
            <div className="form-section">
              <h3 className="section-subtitle">{t('summary.title')}</h3>
              <div className="summary-card">
                <div className="summary-row">
                  <span>{t('summary.plan')}:</span>
                  <span>{isAbonnementPlan ? `${t('plans.items.abonnement.name')} ${tierName[abonnementTier]} — ${t(`plans.frequency.${abonnementFrequency}`)}` : t(`plans.items.${selectedPlan}.name`)}</span>
                </div>
                <div className="summary-row">
                  <span>{t('course.title')}:</span>
                  <span>
                    {selectedCourseIds
                      .map(id => courses.find(c => c.id === id))
                      .filter(Boolean)
                      .map(c => pickL(c.title_i18n ?? c.title, i18n.language, 'fr'))
                      .join(', ') || '—'}
                  </span>
                </div>
                <div className="summary-row">
                  <span>{t('summary.persons')}:</span>
                  <span>{persons.length}</span>
                </div>
                <div className="summary-row">
                  <span>{t('summary.planTotal')}:</span>
                  <span>{baseAmount}€</span>
                </div>
                {selectedPlan !== 'essai' && selectedPlan !== 'unique' && (
                  <div className="summary-row">
                    <span>{t('summary.insurance')}:</span>
                    <span>{formData.insuranceIncluded ? `${25 * persons.length}€` : t('summary.zero')}</span>
                  </div>
                )}
                {(selectedPlan === 'essai' || selectedPlan === 'unique') && (
                  <div className="summary-row note-row">
                    <span>{t('summary.note')}:</span>
                    <span>{selectedPlan === 'essai' ? t('summary.trialNote') : t('summary.uniqueNote')}</span>
                  </div>
                )}
                <div className="summary-row total-row">
                  <span>{t('summary.total')}:</span>
                  <span>{totalAmount}€</span>
                </div>
              </div>
            </div>
          )}

          <div className="payment-section">
            <h3 className="section-subtitle">{t('payment.title')}</h3>
            {selectedPlan === 'essai' ? (
              <p className="info-text">
                {t('payment.trialMollieNote', { defaultValue: 'Paiement en ligne sécurisé (carte, Bancontact) via Mollie — vous serez redirigé(e) après validation de ce formulaire.' })}
              </p>
            ) : (
              <PaymentForm
                amount={totalAmount}
                onPaymentMethodChange={handlePaymentMethodChange}
                defaultMethod="bank"
              />
            )}
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('submit.loading') : t('submit.cta')}
            </button>
          </div>

          {submitSuccess && (
            <div className="success-message">
              {t('submit.successMsg')}
            </div>
          )}
        </form>
      )}

      {/* ── Modal : rejoindre la liste d'attente d'un cours complet ── */}
      {waitlistModal.open && (
        <div className="waitlist-modal-overlay" onClick={closeWaitlist}>
          <div className="waitlist-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="waitlist-modal-header">
              <h3 className="waitlist-modal-title">
                {t('waitlist.title', { defaultValue: "Liste d'attente" })}
                {waitlistModal.courseTitle ? ` — ${waitlistModal.courseTitle}` : ''}
              </h3>
              <button type="button" className="close-button" onClick={closeWaitlist}>&times;</button>
            </div>

            {waitlistSuccess ? (
              <div className="success-message">
                {t('waitlist.successMsg', { defaultValue: 'Merci ! Tu es sur la liste d\'attente, on te recontacte dès qu\'une place se libère.' })}
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit}>
                <p className="info-text" style={{ marginLeft: 0, marginBottom: 15 }}>
                  {t('waitlist.hint', { defaultValue: 'Ce cours est complet. Laisse tes coordonnées, on te contacte dès qu\'une place se libère.' })}
                </p>
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('fields.firstName', { defaultValue: 'Prénom' })} *</label>
                    <input
                      className="form-input" name="firstName" required
                      value={waitlistForm.firstName} onChange={handleWaitlistFieldChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('fields.lastName', { defaultValue: 'Nom' })} *</label>
                    <input
                      className="form-input" name="lastName" required
                      value={waitlistForm.lastName} onChange={handleWaitlistFieldChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('fields.email', { defaultValue: 'E-mail' })} *</label>
                    <input
                      className="form-input" type="email" name="email" required
                      value={waitlistForm.email} onChange={handleWaitlistFieldChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('fields.phone', { defaultValue: 'Téléphone' })}</label>
                    <input
                      className="form-input" type="tel" name="phone"
                      value={waitlistForm.phone} onChange={handleWaitlistFieldChange}
                    />
                  </div>
                </div>
                {waitlistError && <p className="error-text">{waitlistError}</p>}
                <div className="form-actions">
                  <button type="submit" className="submit-button" disabled={waitlistSubmitting}>
                    {waitlistSubmitting
                      ? t('submit.loading')
                      : t('waitlist.submitCta', { defaultValue: "Rejoindre la liste d'attente" })}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Registration;